/**
 * Navigation utilities for preventing duplicate navigation errors
 * and providing safe navigation methods throughout the application
 */

/**
 * Safely navigate to a route using push
 * @param {Object} router - Vue Router instance
 * @param {String|Object} location - Route location (string path or route object)
 * @returns {Promise} Navigation promise that resolves safely
 */
export const safePush = (router, location) => {
  return router.push(location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      console.error('Navigation error:', err);
      return Promise.reject(err);
    }
    // Silently resolve duplicate navigation attempts
    return Promise.resolve();
  });
};

/**
 * Safely navigate to a route using replace
 * @param {Object} router - Vue Router instance
 * @param {String|Object} location - Route location (string path or route object)
 * @returns {Promise} Navigation promise that resolves safely
 */
export const safeReplace = (router, location) => {
  return router.replace(location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      console.error('Navigation error:', err);
      return Promise.reject(err);
    }
    // Silently resolve duplicate navigation attempts
    return Promise.resolve();
  });
};

/**
 * Check if navigation would result in a duplicate
 * @param {Object} router - Vue Router instance
 * @param {String|Object} location - Route location to check
 * @returns {Boolean} True if navigation would be a duplicate
 */
export const isNavigationDuplicate = (router, location) => {
  const current = router.currentRoute;

  // Handle string locations
  if (typeof location === 'string') {
    return current.path === location;
  }

  // Handle object locations
  if (typeof location === 'object') {
    const { name, path, query = {}, params = {} } = location;

    // Check by name
    if (name && current.name === name) {
      return JSON.stringify(current.query) === JSON.stringify(query) && JSON.stringify(current.params) === JSON.stringify(params);
    }

    // Check by path
    if (path && current.path === path) {
      return JSON.stringify(current.query) === JSON.stringify(query);
    }
  }

  return false;
};

/**
 * Navigate only if it's not a duplicate
 * @param {Object} router - Vue Router instance
 * @param {String|Object} location - Route location
 * @param {String} method - Navigation method ('push' or 'replace')
 * @returns {Promise} Navigation promise or resolved promise if duplicate
 */
export const conditionalNavigate = (router, location, method = 'push') => {
  if (isNavigationDuplicate(router, location)) {
    return Promise.resolve();
  }

  return method === 'replace' ? safeReplace(router, location) : safePush(router, location);
};

/**
 * Mixin for Vue components to easily use safe navigation methods
 */
export const NavigationMixin = {
  methods: {
    safePush(location) {
      return safePush(this.$router, location);
    },

    safeReplace(location) {
      return safeReplace(this.$router, location);
    },

    conditionalNavigate(location, method = 'push') {
      return conditionalNavigate(this.$router, location, method);
    },

    isNavigationDuplicate(location) {
      return isNavigationDuplicate(this.$router, location);
    }
  }
};
